import type { ReactElement } from 'react';
import { MenuButton } from './Overlays';

export interface PickerOption<V extends string = string> {
  value: V;
  label: string;
  /** One line under the label: what choosing it means. */
  sub?: string;
  /** A choice that takes something away — "Remove" — set apart at the end, in red. */
  danger?: boolean;
  /** Can be seen and not chosen, and says why. */
  disabledBecause?: string;
}

/**
 * One value out of several, chosen from the app's own menu — never the
 * browser's. Vladimir, 2026-09-25: *"Selects on the meetings page shall have
 * search and look not like basic select but like our own design system
 * component, check other places in the app and replace basic select"*.
 *
 * At rest it is a field: the value, and a caret that says it opens. Open, it
 * is the menu every other choice in the app uses — the value in force ticked
 * and tinted, a filter as soon as the list is long (or whenever `search` says
 * the choice is a search: a person, a series), arrows and Enter and Escape.
 *
 * Named for a screen reader and a test as "<label>: <value>", and the value is
 * on the button as `data-value`.
 */
export function Picker<V extends string>({
  value,
  onChange,
  options,
  label,
  'aria-label': ariaLabel,
  hint,
  search = false,
  disabled = false,
  width,
  size = 'md',
  align = 'left',
  placeholder = 'Choose…',
  wrapClassName,
}: {
  value: V;
  onChange: (value: V) => void;
  options: ReadonlyArray<PickerOption<V>>;
  /** Shown above it, as a field's label is. */
  label?: string;
  /** Its name when nothing on screen says it. */
  'aria-label'?: string;
  hint?: string;
  search?: boolean;
  disabled?: boolean;
  width?: number | string;
  size?: 'sm' | 'md';
  align?: 'left' | 'right';
  /** Said when the value is none of the options. */
  placeholder?: string;
  wrapClassName?: string;
}): ReactElement {
  const current = options.find((o) => o.value === value);
  const shown = current?.label ?? placeholder;
  const name = label ?? ariaLabel;
  const menu = (
    <MenuButton
      label={<span className="picker-value">{shown}</span>}
      align={align}
      size={size}
      search={search}
      items={options.map((o) => ({
        id: o.value,
        label: o.label,
        sub: o.sub,
        danger: o.danger,
        disabledBecause: o.disabledBecause,
        current: o.value === value,
        // The value in force again is no change, as with a select.
        onSelect: () => {
          if (o.value !== value) onChange(o.value);
        },
      }))}
      trigger={{
        className: `picker ${size === 'sm' ? 'picker-sm' : ''}`,
        ariaLabel: name ? `${name}: ${shown}` : undefined,
        disabled,
        width,
        value,
      }}
    />
  );
  const field =
    label || hint ? (
      <div className="field-wrap">
        {label ? <span className="field-label">{label}</span> : null}
        {menu}
        {hint ? <span className="field-hint">{hint}</span> : null}
      </div>
    ) : (
      menu
    );
  // ⚠ Its own element: a page's layout rule (the phone's place menu is hidden
  // on a desktop) must not meet the menu's inline display and lose.
  return wrapClassName ? <div className={wrapClassName}>{field}</div> : field;
}
